import React from 'react';
import { Button, Col, Form, Input, Row, Space, Typography } from 'antd';
// Helpers
import {
  ExternalTalentFormValues,
  PendingPlaceFields,
} from 'helpers/externalTalents';
// Components
import PlaceInput from 'components/common/PlaceInput/PlaceInput';

const { Text } = Typography;

type LocationCardProps = {
  cityFieldLabel: string;
  countryFieldName: keyof ExternalTalentFormValues;
  countryFieldLabel: string;
  currentPendingLocation: PendingPlaceFields | null;
  disableBrowserAutocomplete?: boolean;
  onApplySuggestion: () => void;
  onDismissSuggestion: () => void;
  onPlaceSelect: (placeDetails: any) => void;
  onSearchChange: () => void;
  placeFieldName: keyof ExternalTalentFormValues;
  placeLabel: string;
  placePlaceholder: string;
  searchInputName: string;
  searchPrefix: string;
  stateFieldName: keyof ExternalTalentFormValues;
  stateFieldLabel: string;
  cityFieldName: keyof ExternalTalentFormValues;
};

const cardStyle: React.CSSProperties = {
  background: '#fafafa',
  border: '1px solid #f0f0f0',
  borderRadius: 12,
  marginBottom: 16,
  padding: 16,
};

const suggestionStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #f0f0f0',
  borderRadius: 8,
  marginTop: 12,
  padding: 12,
};

const LocationCard = ({
  cityFieldLabel,
  cityFieldName,
  countryFieldName,
  countryFieldLabel,
  currentPendingLocation,
  disableBrowserAutocomplete = false,
  onApplySuggestion,
  onDismissSuggestion,
  onPlaceSelect,
  onSearchChange,
  placeFieldName,
  placeLabel,
  placePlaceholder,
  searchInputName,
  searchPrefix,
  stateFieldName,
  stateFieldLabel,
}: LocationCardProps): JSX.Element => {
  const country =
    currentPendingLocation?.homeCountry || currentPendingLocation?.country;
  const state =
    currentPendingLocation?.homeState || currentPendingLocation?.state;
  const city = currentPendingLocation?.homeCity || currentPendingLocation?.city;

  return (
    <div style={cardStyle}>
      <Row gutter={24}>
        <Col span={10}>
          <Form.Item label={placeLabel} name={placeFieldName}>
            <PlaceInput
              autocompleteBlockerPrefix={searchPrefix}
              disableBrowserAutocomplete={disableBrowserAutocomplete}
              name={searchInputName}
              onPlaceSelect={onPlaceSelect}
              onSearchChange={onSearchChange}
              placeholder={placePlaceholder}
            />
          </Form.Item>

          <Text type="secondary">
            Autocomplete now previews a suggestion first. You can still ignore
            it and enter the fields on the right manually.
          </Text>

          {!currentPendingLocation ? (
            <Text type="secondary">
              Choose a suggestion to preview it, then apply it to the fields on
              the right.
            </Text>
          ) : (
            <div style={suggestionStyle}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>
                Suggested values
              </Text>

              <Text style={{ display: 'block' }}>
                Country: {country || '-'}
              </Text>
              <Text style={{ display: 'block' }}>State: {state || '-'}</Text>
              <Text style={{ display: 'block', marginBottom: 12 }}>
                City: {city || '-'}
              </Text>

              <Space>
                <Button onClick={onApplySuggestion} type="primary">
                  Apply suggestion
                </Button>

                <Button onClick={onDismissSuggestion}>Dismiss</Button>
              </Space>
            </div>
          )}
        </Col>

        <Col span={14}>
          <Form.Item label={countryFieldLabel} name={countryFieldName}>
            <Input />
          </Form.Item>

          <Form.Item label={stateFieldLabel} name={stateFieldName}>
            <Input />
          </Form.Item>

          <Form.Item label={cityFieldLabel} name={cityFieldName}>
            <Input />
          </Form.Item>
        </Col>
      </Row>
    </div>
  );
};

export default LocationCard;
